import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Heading, Spinner, VStack, Box, Alert, AlertIcon, AlertTitle, AlertDescription, Button, HStack, Badge, Text } from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import { api } from '../lib/api';
import { haptic } from '../telegram';

// @ts-ignore
import '../algomed-styles/lecture-content.css';

interface LectureData {
  id: number;
  title: string;
  content: string;
  topic?: { id: number; name: string };
}

export default function LecturePage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [data, setData] = useState<LectureData | null>(null);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id || id === 'list') {
      setError({ message: 'Konu Anlatımı doğrudan listelenemez. Botun "Konu Anlatımı" menüsünden bir lecture seç.' });
      setLoading(false);
      return;
    }
    setLoading(true);
    api.lecture(Number(id))
      .then((d) => { setData(d); haptic('success'); })
      .catch((e) => {
        haptic('error');
        if (e?.status === 403) setError({ status: 403, message: 'Bu konu anlatımı SILVER ve üzeri planlarda açık.' });
        else if (e?.status === 401) setError({ status: 401, message: 'Yetki doğrulanamadı. Mini App\'i Telegram içinden açtığından emin ol.' });
        else if (e?.status === 404) setError({ status: 404, message: 'Bu konu anlatımı bulunamadı.' });
        else setError({ message: 'Konu yüklenemedi. Bağlantını kontrol et.' });
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <VStack p={12} spacing={3}>
        <Spinner size="xl" color="accent" thickness="3px" />
        <Text color="textSecondary">Konu yükleniyor…</Text>
      </VStack>
    );
  }

  if (error) {
    return (
      <Container maxW="md" py={6}>
        <Alert status={error.status === 403 ? 'info' : 'error'} borderRadius="md" flexDirection="column" alignItems="flex-start">
          <HStack mb={2}>
            <AlertIcon />
            <AlertTitle>{error.status === 403 ? 'Premium içerik' : 'Hata'}</AlertTitle>
          </HStack>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
        <HStack mt={4} spacing={2}>
          {error.status === 403 && (
            <Button colorScheme="brand" onClick={() => nav('/subscription')}>💳 Planı Yükselt</Button>
          )}
          <Button variant="outline" onClick={() => window.location.reload()}>Tekrar dene</Button>
          <Button variant="ghost" onClick={() => nav('/')}>Ana sayfa</Button>
        </HStack>
      </Container>
    );
  }

  if (!data) return null;

  return (
    <Container maxW="2xl" py={4} px={4}>
      <VStack align="stretch" spacing={3}>
        {data.topic && <HStack><Badge colorScheme="blue">{data.topic.name}</Badge></HStack>}
        <Heading size="lg" lineHeight="1.3">{data.title}</Heading>
        <Box
          className="lecture-reader"
          fontSize="md"
          lineHeight="1.7"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.content) }}
        />
        <Box pt={4} pb={8}>
          <Button w="full" variant="outline" onClick={() => nav('/')}>Ana sayfaya dön</Button>
        </Box>
      </VStack>
    </Container>
  );
}
